import { request } from "../../../functions/request";

/**
 * Banco de modelos do gerador.
 *
 * O spec inteiro vai serializado em grf_config; grf_modelo é a família,
 * repetida fora do JSON para a galeria poder filtrar sem abrir cada blob.
 * O envelope { newData } é a convenção de escrita do resto do sistema.
 */

export async function listarModelos() {
  const resposta = await request("/modelo_grafico/getModelosGraficoAtivos", {
    method: "GET",
  });
  return (Array.isArray(resposta) ? resposta : []).map((linha) => ({
    codigo: linha.grf_codigo,
    nome: linha.grf_nome,
    familia: linha.grf_modelo,
    spec: interpretarSpec(linha.grf_config),
  }));
}

export async function salvarModelo(nome, spec) {
  return request("/modelo_grafico/insertModeloGrafico", {
    method: "POST",
    body: JSON.stringify({
      newData: {
        grf_nome: nome,
        grf_modelo: spec.modelo,
        grf_config: JSON.stringify(spec),
        grf_ativo: 1,
      },
    }),
  });
}

export async function atualizarModelo(codigo, nome, spec) {
  return request(`/modelo_grafico/updateModeloGrafico/${codigo}`, {
    method: "PUT",
    body: JSON.stringify({
      newData: {
        grf_nome: nome,
        grf_modelo: spec.modelo,
        grf_config: JSON.stringify(spec),
        grf_ativo: 1,
      },
    }),
  });
}

export async function excluirModelo(codigo) {
  return request(`/modelo_grafico/deleteModeloGrafico/${codigo}`, {
    method: "DELETE",
  });
}

/**
 * Um modelo com JSON corrompido não pode derrubar a listagem inteira —
 * ele é descartado e os outros continuam aparecendo.
 */
function interpretarSpec(texto) {
  try {
    return JSON.parse(texto);
  } catch (err) {
    console.error("Modelo de gráfico com JSON inválido, ignorado:", err);
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * Banco de imagens de fundo
 * ------------------------------------------------------------------ */

export async function listarFundos() {
  const resposta = await request("/modelo_grafico/getFundos", { method: "GET" });
  return (Array.isArray(resposta) ? resposta : []).map((linha) => ({
    codigo: linha.gfu_codigo,
    nome: linha.gfu_nome,
  }));
}

/** Busca o conteudo de um fundo (data URI). Pesado — so sob demanda. */
export async function carregarFundo(codigo) {
  const resposta = await request(`/modelo_grafico/getFundo/${codigo}`, { method: "GET" });
  return resposta?.gfu_imagem || null;
}

export async function salvarFundo(nome, dataUri) {
  return request("/modelo_grafico/insertFundo", {
    method: "POST",
    body: JSON.stringify({ newData: { gfu_nome: nome, gfu_imagem: dataUri } }),
  });
}

export async function excluirFundo(codigo) {
  return request(`/modelo_grafico/deleteFundo/${codigo}`, { method: "DELETE" });
}

/**
 * Reduz a imagem escolhida para o tamanho exato de saida antes de guardar.
 *
 * Duas razoes: uma foto de 4 MB viraria um data URI de 5,5 MB dentro do
 * banco e dentro de cada SVG; e o recorte "cobrir" garante que o fundo
 * preencha o quadro sem distorcer, seja qual for a proporcao do original.
 */
export function prepararFundo(arquivo, largura = 604, altura = 366) {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error("Nao foi possivel ler o arquivo."));
    leitor.onload = async () => {
      try {
        const img = new Image();
        img.src = leitor.result;
        await img.decode();

        const canvas = document.createElement("canvas");
        canvas.width = largura;
        canvas.height = altura;
        const ctx = canvas.getContext("2d");

        const escala = Math.max(largura / img.width, altura / img.height);
        const l = img.width * escala;
        const a = img.height * escala;
        ctx.drawImage(img, (largura - l) / 2, (altura - a) / 2, l, a);

        resolve(canvas.toDataURL("image/jpeg", 0.82));
      } catch (err) {
        reject(new Error("Arquivo de imagem invalido."));
      }
    };
    leitor.readAsDataURL(arquivo);
  });
}
